import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Shown above the stack. e.g. "Pipeline crashed". */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Simple error boundary that surfaces the actual message + component stack
 * in the DOM instead of silently unmounting to a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.label ?? "", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 20,
            margin: 20,
            background: "var(--neo-red-bg)",
            border: "1px solid rgba(200,50,50,0.4)",
            borderRadius: 8,
            color: "var(--neo-red)",
            fontFamily: "'DM Mono', ui-monospace, monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            overflowX: "auto",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
            {this.props.label ?? "Something went wrong"}
          </div>
          <div style={{ marginBottom: 10 }}>
            <strong>{this.state.error.name}:</strong>{" "}
            {this.state.error.message}
          </div>
          {this.state.error.stack && (
            <details>
              <summary style={{ cursor: "pointer", marginBottom: 6 }}>
                Stack trace
              </summary>
              <pre style={{ fontSize: 11, opacity: 0.8 }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          <button
            onClick={() => this.setState({ error: null })}
            style={{
              marginTop: 10,
              padding: "4px 10px",
              fontSize: 11,
              background: "var(--neo-red)",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
